import { useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';

import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';

import {
    useBookMeetingGate,
    useBookMeetingGateEnabled,
} from '../book-meeting-gate-state';
import { useBookMeetingGateController } from '../hooks/use-book-meeting-gate-controller';

/**
 * Booking in the revamped two-pane gate: [the form's questions] → [person and
 * day] → [time] → [your details] → [sign in] → [booked or requested].
 *
 * Every entry point opens this one modal, so a CTA anywhere on the page — the
 * detail-page accordion row, the profile header, an Our Team card — is a call
 * to {@link useOpenBookMeetingGate} with an intent, and nothing else.
 *
 * Details first, auth last: the reader answers, picks a time and says who they
 * are before anything asks them to sign in, so the account is the last step
 * rather than the price of looking. The journey itself lives in
 * {@link useBookMeetingGateController}; this is only its dialog.
 */
export const BookMeetingGateModal = () => {
    // The gate's atoms are sessionStorage-backed, so an intent left over from a
    // flag-on session would otherwise open this on a build meant to have no
    // gate at all. Every other gate is mounted behind the flag for this reason.
    // Same condition the CTAs read, so a CTA can never set an intent that
    // nothing mounts to answer.
    const enabled = useBookMeetingGateEnabled();
    if (!enabled) return null;

    return <BookMeetingGateModalInner />;
};

const BookMeetingGateModalInner = () => {
    const { intent, isOpen, reopen, close } = useBookMeetingGate();
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    /** Last intent shown, so the sheet can animate out after it is cleared. */
    const lastIntentRef = useRef<typeof intent>(null);

    // Written from an effect, not during render: a render-phase ref write can
    // be committed by a pass React later discards, and this ref decides what
    // the sheet shows on its way out.
    useEffect(() => {
        if (intent) lastIntentRef.current = intent;
    }, [intent]);

    const view = useBookMeetingGateController({
        intent: intent ?? lastIntentRef.current,
        isOpen,
        onClose: close,
        onReopen: reopen,
    });
    if (!view) return null;

    return (
        <TwoPaneModal
            corners="rounded"
            open={isOpen}
            onOpenChange={(nextOpen) => {
                if (nextOpen || view.dismissBlocked) return;
                view.onDismiss();
            }}
            dismissible={!view.dismissBlocked}
            onPointerDownOutside={(event) => {
                if (view.dismissBlocked) event.preventDefault();
            }}
            scrollResetKey={view.scrollResetKey}
            // Every step pins its action to the bottom, as the enquiry gate's do: the
            // scroll hint and fade sit above that bar rather than behind it.
            scrollHintClassName={isMobile ? 'bottom-24' : 'bottom-23.5'}
            title={view.title}
            pane={view.pane}
        >
            {view.content}
        </TwoPaneModal>
    );
};
